import * as React from "react";
import styled, { css } from "styled-components";
import useWindowSize from "../../../hooks/useWindowSize";
import { ReactComponent as ChevronLeft } from "../../../assets/img/illustrations/ArrowLeft.svg";
import { ReactComponent as ChevronRight } from "../../../assets/img/illustrations/ArrowRight.svg";
import { useScrollIntoView } from "../Tabs/utils";

const Wrapper = styled.div`
  display: flex;
  align-items: center;
  column-gap: 16px;
`;

const Slider = styled.div`
  display: flex;
  column-gap: ${({ columnGap }) => (columnGap ? `${columnGap}px` : "8px")};
  flex-wrap: nowrap;
  white-space: nowrap;
  width: auto;
  overflow-x: auto;
  scroll-behavior: smooth;
  justify-content: ${({ isCenter }) => (isCenter ? "center" : "")};

  &::-webkit-scrollbar {
    display: none;
  }

  @media only screen and (min-width: 768px) {
    column-gap: 21px;
  }
`;

const ButtonArrow = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 16px;
  background-color: ${({ isDarkMode }) => (isDarkMode ? "#1F2D3D" : "#fff")};
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0px 3px 5px 0px #0000000d;
  box-shadow: 0px 1px 18px 0px #0000000d;
  box-shadow: 0px 6px 10px 0px #0000000d;
  cursor: pointer;
  opacity: ${({ opacity }) => opacity ?? "1"};
  transform: opacity 0.3s;

  &:hover {
    opacity: 1;
  }

  ${({ isDisabled, isDarkMode }) =>
    isDisabled && !isDarkMode
      ? css`
          svg path {
            fill: #dde2e8;
          }
        `
      : isDarkMode && !isDisabled
      ? css`
          svg path {
            fill: white;
          }
        `
      : css`
          svg path {
            fill: #6a7481;
          }
        `}
`;

const SliderDotsContainer = styled.div`
  display: flex;
  justify-content: center;
  column-gap: 4px;
  margin-top: 16px;
`;

export const Dots = styled.div`
  width: ${(props) => (props?.isActive ? "16px" : "8px")};
  height: 8px;
  border-radius: 8px;
  background-color: ${(props) =>
    props?.isActive ? props?.activeDotColor ?? "grey" : "#B8C1CB"};
`;

const SliderWrapper = ({
  children,
  cardWrapperStyle,
  showItems,
  indicatorStyle,
  totalData,
  style,
  showDotsIndicator = true,
  swipeable = false,
  gap = 12,
  step = 1,
  columnGap = 8,
  activeDotColor = "grey",
  showArrow = true,
  isUseEndSpace = false,
  endSpaceWidth = "20px",
  arrowOpacity = "1",
  forceTabPosition = 0,
  isForceTabPosition = false,
  isDarkMode = false,
  isCenter = false,
  callbackChangeTabPosition = () => {},
}) => {
  const { size } = useWindowSize();
  const isDesktop = size?.width > 768;

  let timeout;

  const tabPositionNumber = isForceTabPosition ? forceTabPosition : 0;

  const containerRef = React.useRef();
  const wrapperRef = React.useRef();
  const touchPositionRef = React.useRef(0);
  const mouseDownRef = React.useRef();
  const childrenWidthRef = React.useRef(0);
  const tabPositionRef = React.useRef(tabPositionNumber);
  const currentPositionRef = React.useRef(0);

  const [arrayOfTabWidth, setArrayOfTabWidth] = React.useState([]);
  const [tabPosition, setTabPosition] = React.useState(tabPositionNumber);

  const [isLongPress, setIsLongPress] = React.useState(false);

  const { scrollTo } = useScrollIntoView();

  const DotsArray = React.useMemo(() => {
    if (totalData <= 0 || showItems <= 0) return [];

    let dotsData = Math.ceil(totalData / showItems);

    if (dotsData >= 0) {
      return Array(dotsData)
        .fill(null)
        ?.map((_, idx) => idx);
    }

    return [];
  }, [step, totalData, showItems]);

  const showIndicator = React.useMemo(() => {
    return showArrow;
  }, [showItems, totalData]);

  const disabledRightButton = React.useMemo(() => {
    return tabPosition >= DotsArray?.length - 1;
  }, [DotsArray, tabPosition]);

  const _clientWidth = wrapperRef?.current?.clientWidth;
  const _clientCardWidth = Math.ceil(_clientWidth / showItems) - gap;

  const getTouchPosition = (event) => {
    event.persist();
    if (window.TouchEvent && event.nativeEvent instanceof TouchEvent) {
      return event.nativeEvent.touches[0]
        ? event.nativeEvent.touches[0].clientX
        : event.nativeEvent.changedTouches[0].clientX;
    }

    if (event.nativeEvent instanceof MouseEvent) {
      return event.nativeEvent.screenX;
    }

    return 0;
  };

  const handleMouseDown = React.useCallback((event) => {
    if (!swipeable) return;

    mouseDownRef.current = true;

    const mouseDown = getTouchPosition(event);

    touchPositionRef.current = mouseDown;
  }, []);

  const handleMouseMove = React.useCallback(
    (event) => {
      if (!isLongPress) return;
      if (!swipeable) return;

      event?.stopPropagation();

      if (!touchPositionRef?.current && !mouseDownRef?.current) return;

      const mouseMove = getTouchPosition(event);

      const scrollToLeft =
        Math.abs(mouseMove) - Math.abs(touchPositionRef?.current);

      if (wrapperRef?.current && mouseDownRef?.current)
        wrapperRef.current.scrollLeft += scrollToLeft;
    },
    [isLongPress]
  );

  const handleMouseUp = React.useCallback((_) => {
    if (!swipeable) return;

    mouseDownRef.current = false;
    touchPositionRef.current = false;
  }, []);

  const handleClickArrowStart = React.useCallback(
    (event) => {
      const isDisabled = event?.target?.dataset?.disabled === "true";
      if (!wrapperRef?.current || isDisabled) return;

      if (wrapperRef?.current) {
        const _tabIdx = tabPositionRef?.current - 1;
        const _left = Number(arrayOfTabWidth?.[_tabIdx]?.width ?? 0);

        scrollTo({
          top: 0,
          left: _left,
          horizontal: true,
          averageWidth: childrenWidthRef,
          scrollableContainer: wrapperRef?.current,
          /** ketika mendapatkan error ataupun ternyata tab bukan scrolling element maka bisa menggunakan callback ini */
          errorCallback: () => {
            tabPositionRef = _tabIdx;
          },
        })?.then((_) => {
          tabPositionRef.current = _tabIdx;
          setTabPosition(_tabIdx);
          callbackChangeTabPosition(_tabIdx);
        });
      }
    },
    [arrayOfTabWidth]
  );

  const handleClickArrowEnd = React.useCallback(
    (event) => {
      const isDisabled = event?.target?.dataset?.disabled === "true";
      if (
        !wrapperRef?.current ||
        isDisabled ||
        tabPosition >= DotsArray?.length - 1
      )
        return;

      if (wrapperRef?.current) {
        const _tabIdx = tabPositionRef?.current + 1 * step;

        const _left = Number(arrayOfTabWidth?.[_tabIdx]?.width ?? 0);
        const isEndOfContent =
          _tabIdx >= DotsArray?.length - 1 && showItems > 1;
        const isFirstContent = _tabIdx <= 0;

        const generateLeftPos = () => {
          if (!isDesktop && !isFirstContent) return _left + gap;
          if (isFirstContent) return 0;
          if (isEndOfContent)
            return _clientWidth < _left + gap ? _left + gap : _clientWidth;

          return _left + gap;
        };

        scrollTo({
          top: 0,
          left: generateLeftPos(),
          horizontal: true,
          averageWidth: childrenWidthRef?.current,
          scrollableContainer: wrapperRef?.current,
          /** ketika mendapatkan error ataupun ternyata tab bukan scrolling element maka bisa menggunakan callback ini */
          errorCallback: () => {
            tabPositionRef = _tabIdx;
          },
        })?.then((_) => {
          tabPositionRef.current = _tabIdx;
          setTabPosition(_tabIdx);
          callbackChangeTabPosition(_tabIdx);
        });
      }
    },
    [arrayOfTabWidth, disabledRightButton]
  );

  const handleTouchStart = React.useCallback((event) => {
    if (!swipeable) return;

    const touchDown = getTouchPosition(event);

    // set touch start position
    touchPositionRef.current = touchDown;
  }, []);

  const handleTouchMove = React.useCallback(
    (event) => {
      if (!swipeable && tabPosition >= DotsArray?.length - 1) return;

      event?.stopPropagation();
      const currentPosition = getTouchPosition(event);

      currentPositionRef.current = currentPosition;
    },
    [isLongPress]
  );

  const handleTouchEnd = React.useCallback(
    (_) => {
      if (!swipeable && !tabPositionRef) return;

      clearTimeout(timeout);
      setIsLongPress(false);

      let threshold = 20;

      const touchDiffX =
        touchPositionRef?.current - currentPositionRef?.current;

      if (Math.abs(touchDiffX) <= threshold) return;

      const currentPosition = () => {
        const dataTotal = Math.ceil(totalData / showItems);

        if (tabPosition >= dataTotal) return tabPositionRef.current;

        if (touchDiffX < 0) {
          if (tabPositionRef?.current <= 0) return tabPositionRef.current;
          return tabPositionRef.current - 1;
        }

        return tabPositionRef.current + 1;
      };

      const _tabIdx = currentPosition();
      const _left = Number(arrayOfTabWidth?.[_tabIdx]?.width ?? 0);
      const isEndOfContent =
        _tabIdx >= DotsArray?.length - 1 && DotsArray % 2 !== 0;

      const isFirstContent = _tabIdx <= 0;

      if (_tabIdx > DotsArray?.length - 1) {
        return;
      }

      const generateLeftPos = () => {
        if (!isDesktop && !isFirstContent) return _left + gap;
        if (isFirstContent) return 0;
        if (isEndOfContent) return _clientWidth;

        return _left + gap;
      };

      scrollTo({
        top: 0,
        left: generateLeftPos(),
        horizontal: true,
        averageWidth: childrenWidthRef?.current + gap,
        scrollableContainer: wrapperRef?.current,
        /** ketika mendapatkan error ataupun ternyata tab bukan scrolling element maka bisa menggunakan callback ini */
        errorCallback: () => {
          if (tabPositionRef && _tabIdx) {
            tabPositionRef = _tabIdx;
          }
        },
      })?.then((_) => {
        tabPositionRef.current = _tabIdx;
        setTabPosition(_tabIdx);
        callbackChangeTabPosition(_tabIdx);
      });
    },
    [arrayOfTabWidth, isLongPress]
  );

  React.useEffect(() => {
    if (totalData > 0 && wrapperRef?.current && !!_clientCardWidth) {
      const _clientChildren = wrapperRef?.current?.children;

      if (typeof _clientChildren === "object" && _clientChildren?.length > 0) {
        childrenWidthRef.current = _clientChildren?.[0]?.clientWidth + gap;

        let _childrenWidth = [];
        let _childrenAccumulativeWidth = 0;

        for (let i = 0; i < totalData; i++) {
          _childrenWidth?.push({
            id: i,
            width: _childrenAccumulativeWidth,
            clientWidth: _clientChildren?.[i]?.clientWidth,
          });

          _childrenAccumulativeWidth += _clientCardWidth;
        }

        if (_childrenWidth?.length > 0) setArrayOfTabWidth(_childrenWidth);
      }
    }

    return () => {
      touchPositionRef.current = 0;
      childrenWidthRef.current = 0;
      tabPositionRef.current = 0;
    };
  }, [totalData, _clientCardWidth]);

  React.useEffect(() => {
    if (wrapperRef?.current) {
      wrapperRef.current.addEventListener("touchstart", (event) => {
        timeout = setTimeout(() => {
          setIsLongPress(true);
        }, 300);
      });
    }
  }, []);

  return (
    <div style={style} ref={containerRef}>
      {showArrow ? (
        <Wrapper>
          {isDesktop && showIndicator && showArrow && (
            <ButtonArrow
              opacity={arrowOpacity}
              isDisabled={tabPosition <= 0}
              data-disabled={tabPosition <= 0}
              onClick={handleClickArrowStart}
              isDarkMode={isDarkMode}
            >
              <ChevronLeft />
            </ButtonArrow>
          )}
          <Slider
            isCenter={isCenter && !showIndicator}
            ref={wrapperRef}
            onMouseDown={swipeable ? handleMouseDown : undefined}
            onMouseMove={swipeable ? handleMouseMove : undefined}
            onMouseUp={swipeable ? handleMouseUp : undefined}
            onMouseLeave={swipeable ? handleMouseUp : undefined}
            onTouchStart={swipeable ? handleTouchStart : undefined}
            onTouchMove={swipeable ? handleTouchMove : undefined}
            onTouchEnd={
              swipeable ? (isLongPress ? handleTouchEnd : () => {}) : undefined
            }
            style={cardWrapperStyle}
            className="slider-custom"
            columnGap={columnGap}
          >
            {children({
              parentWidth: _clientWidth,
              cardWidth: _clientCardWidth,
            })}
            {isUseEndSpace && (
              <div style={{ width: endSpaceWidth, userSelect: "none" }} />
            )}
          </Slider>
          {isDesktop && showIndicator && showArrow && (
            <ButtonArrow
              opacity={arrowOpacity}
              isDisabled={disabledRightButton}
              data-disabled={disabledRightButton}
              onClick={handleClickArrowEnd}
              isDarkMode={isDarkMode}
            >
              <ChevronRight />
            </ButtonArrow>
          )}
        </Wrapper>
      ) : (
        <Slider
          isCenter={isCenter && !showIndicator}
          ref={wrapperRef}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={isLongPress ? handleTouchEnd : () => {}}
          style={cardWrapperStyle}
          className="slider-custom"
          columnGap={columnGap}
        >
          {children({
            parentWidth: _clientWidth,
            cardWidth: _clientCardWidth,
          })}
          {isUseEndSpace && (
            <div style={{ width: endSpaceWidth, userSelect: "none" }} />
          )}
        </Slider>
      )}
      {showIndicator && showDotsIndicator && totalData > showItems && (
        <SliderDotsContainer style={indicatorStyle}>
          {DotsArray?.map((data) => (
            <Dots
              activeDotColor={activeDotColor}
              key={data}
              isActive={data === tabPosition}
            />
          ))}
        </SliderDotsContainer>
      )}
    </div>
  );
};

export default SliderWrapper;
